import {
  Delete as DeleteIcon,
  Link as LinkIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Stack,
  TextField,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Chip,
  IconButton as MuiIconButton,
  Alert,
  Tabs,
  Tab,
  Grid,
} from "@mui/material";
import { currencyFormat } from "common/numbers";
import { useState, useMemo } from "react";

const ClaimsTable = ({
  claims,
  selectedIds,
  onToggleSelect,
  searchTerm,
  title,
  linkedIds,
}) => {
  const filteredClaims = useMemo(() => {
    if (!searchTerm) return claims;
    const lower = searchTerm.toLowerCase();
    return claims.filter(
      (claim) =>
        claim.claimant_name?.toLowerCase().includes(lower) ||
        claim.description?.toLowerCase().includes(lower) ||
        claim.coverage?.toLowerCase().includes(lower) ||
        claim.date?.toLowerCase().includes(lower)
    );
  }, [claims, searchTerm]);

  return (
    <Box>
      <Typography variant={"subtitle1"} gutterBottom>
        {title}
      </Typography>
      <TableContainer
        component={Paper}
        sx={{ maxHeight: 180, marginBottom: 1 }}
      >
        <Table stickyHeader size={"small"}>
          <TableHead>
            <TableRow>
              <TableCell padding={"checkbox"} sx={{ height: 32 }} />
              <TableCell sx={{ height: 32 }}>{"Date"}</TableCell>
              <TableCell sx={{ height: 32 }}>{"Coverage"}</TableCell>
              <TableCell sx={{ height: 32 }}>{"Claimant"}</TableCell>
              <TableCell sx={{ height: 32 }}>{"Description"}</TableCell>
              <TableCell align={"right"} sx={{ height: 32 }}>
                {"Amount"}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredClaims.map((claim) => {
              const isLinked = linkedIds.has(claim.claim_id);
              const isSelected = selectedIds.includes(claim.claim_id);
              return (
                <TableRow
                  key={claim.claim_id}
                  hover
                  selected={isSelected}
                  sx={{
                    height: 32,
                    opacity: isLinked ? 0.5 : 1,
                    backgroundColor: isSelected
                      ? "action.selected"
                      : isLinked
                      ? "action.disabledBackground"
                      : "inherit",
                  }}
                >
                  <TableCell padding={"checkbox"} sx={{ height: 32 }}>
                    <Checkbox
                      checked={isSelected}
                      onChange={() => onToggleSelect(claim.claim_id)}
                      disabled={isLinked}
                      size={"small"}
                    />
                  </TableCell>
                  <TableCell sx={{ height: 32, padding: "4px 8px" }}>
                    {claim.date || "-"}
                  </TableCell>
                  <TableCell sx={{ height: 32, padding: "4px 8px" }}>
                    {claim.coverage || "-"}
                  </TableCell>
                  <TableCell sx={{ height: 32, padding: "4px 8px" }}>
                    {claim.claimant_name || "-"}
                  </TableCell>
                  <TableCell
                    sx={{
                      height: 32,
                      padding: "4px 8px",
                      maxWidth: 200,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {claim.description || "-"}
                  </TableCell>
                  <TableCell
                    align={"right"}
                    sx={{ height: 32, padding: "4px 8px" }}
                  >
                    {claim.total_loss != null
                      ? currencyFormat(claim.total_loss)
                      : "-"}
                  </TableCell>
                </TableRow>
              );
            })}
            {filteredClaims.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align={"center"} sx={{ height: 32 }}>
                  {"No claims found"}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const ManualMatchItem = ({ match, onDelete, priorClaims, currentClaims }) => {
  const priorClaimsList = match.prior_claim_ids
    .map((id) => priorClaims.find((c) => c.claim_id === id))
    .filter(Boolean);
  const currentClaimsList = match.current_claim_ids
    .map((id) => currentClaims.find((c) => c.claim_id === id))
    .filter(Boolean);

  return (
    <Paper variant={"outlined"} sx={{ padding: 1.5, height: "100%" }}>
      <Stack spacing={1} height={"100%"}>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"flex-start"}
        >
          <Stack direction={"row"} spacing={0.5} alignItems={"center"}>
            <Chip
              label={`${priorClaimsList.length} Prior`}
              size={"small"}
              color={"primary"}
            />
            <LinkIcon fontSize={"small"} />
            <Chip
              label={`${currentClaimsList.length} Current`}
              size={"small"}
              color={"secondary"}
            />
          </Stack>
          <MuiIconButton
            onClick={onDelete}
            size={"small"}
            color={"error"}
            sx={{ marginTop: -0.5, marginRight: -0.5 }}
          >
            <DeleteIcon fontSize={"small"} />
          </MuiIconButton>
        </Stack>
        <Paper
          variant={"outlined"}
          sx={{
            padding: 0.75,
            backgroundColor: "grey.50",
            height: "60px",
            overflow: "auto",
          }}
        >
          <Typography
            variant={"caption"}
            color={"text.secondary"}
            sx={{ fontStyle: match.comment ? "normal" : "italic" }}
          >
            {match.comment || "No comment added"}
          </Typography>
        </Paper>
        <Box>
          <Typography variant={"caption"} fontWeight={"bold"}>
            {"Prior Claims:"}
          </Typography>
          {priorClaimsList.map((claim) => (
            <Typography
              key={claim.claim_id}
              variant={"caption"}
              display={"block"}
              sx={{ fontSize: "0.7rem" }}
            >
              {`${claim.date} - ${claim.claimant_name} (${currencyFormat(
                claim.total_loss
              )})`}
            </Typography>
          ))}
        </Box>
        <Box>
          <Typography variant={"caption"} fontWeight={"bold"}>
            {"Current Claims:"}
          </Typography>
          {currentClaimsList.map((claim) => (
            <Typography
              key={claim.claim_id}
              variant={"caption"}
              display={"block"}
              sx={{ fontSize: "0.7rem" }}
            >
              {`${claim.date} - ${claim.claimant_name} (${currencyFormat(
                claim.total_loss
              )})`}
            </Typography>
          ))}
        </Box>
      </Stack>
    </Paper>
  );
};

const ManualClaimsLinkerModal = ({
  open,
  onClose,
  priorClaims,
  currentClaims,
  existingMatches,
  onSaveMatches,
  initialTab = 0,
}) => {
  const [selectedPriorIds, setSelectedPriorIds] = useState([]);
  const [selectedCurrentIds, setSelectedCurrentIds] = useState([]);
  const [comment, setComment] = useState("");
  const [priorSearch, setPriorSearch] = useState("");
  const [currentSearch, setCurrentSearch] = useState("");
  const [manualMatches, setManualMatches] = useState(existingMatches || []);
  const [currentTab, setCurrentTab] = useState(initialTab);

  const linkedPriorIds = useMemo(() => {
    const ids = new Set();
    manualMatches.forEach((match) => {
      match.prior_claim_ids.forEach((id) => ids.add(id));
    });
    return ids;
  }, [manualMatches]);

  const linkedCurrentIds = useMemo(() => {
    const ids = new Set();
    manualMatches.forEach((match) => {
      match.current_claim_ids.forEach((id) => ids.add(id));
    });
    return ids;
  }, [manualMatches]);

  const handleTogglePrior = (claimId) => {
    setSelectedPriorIds((prev) =>
      prev.includes(claimId)
        ? prev.filter((id) => id !== claimId)
        : [...prev, claimId]
    );
  };

  const handleToggleCurrent = (claimId) => {
    setSelectedCurrentIds((prev) =>
      prev.includes(claimId)
        ? prev.filter((id) => id !== claimId)
        : [...prev, claimId]
    );
  };

  const handleCreateMatch = () => {
    if (selectedPriorIds.length === 0 || selectedCurrentIds.length === 0) {
      return;
    }

    const newMatch = {
      prior_claim_ids: selectedPriorIds,
      current_claim_ids: selectedCurrentIds,
      comment: comment.trim(),
      is_manual: true,
      match_id: `manual_${Date.now()}`,
    };

    setManualMatches([...manualMatches, newMatch]);
    setSelectedPriorIds([]);
    setSelectedCurrentIds([]);
    setComment("");
  };

  const handleDeleteMatch = (matchId) => {
    setManualMatches(manualMatches.filter((m) => m.match_id !== matchId));
  };

  const handleSave = () => {
    onSaveMatches(manualMatches);
    onClose();
  };

  const handleClose = () => {
    setSelectedPriorIds([]);
    setSelectedCurrentIds([]);
    setComment("");
    setManualMatches(existingMatches || []);
    setCurrentTab(initialTab);
    onClose();
  };

  const canCreateMatch =
    selectedPriorIds.length > 0 && selectedCurrentIds.length > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth={"xl"} fullWidth>
      <DialogTitle>{"Manual Claims Linking"}</DialogTitle>
      <DialogContent dividers sx={{ height: "560px", overflow: "auto" }}>
        <Stack spacing={2}>
          <Tabs
            value={currentTab}
            onChange={(e, newValue) => setCurrentTab(newValue)}
            sx={{ marginTop: -1, marginBottom: -0.5 }}
          >
            <Tab label={"Create New Link"} />
            <Tab label={`Linked Claims (${manualMatches.length})`} />
          </Tabs>

          {currentTab === 0 && (
            <Stack spacing={1.5}>
              <Alert severity={"info"}>
                {
                  "Select claims from both prior and current periods to manually link them as duplicates. Manual links will override AI-detected matches."
                }
              </Alert>

              <Stack direction={"row"} spacing={2}>
                <Box flex={1}>
                  <TextField
                    fullWidth
                    size={"small"}
                    placeholder={"Search prior claims..."}
                    value={priorSearch}
                    onChange={(e) => setPriorSearch(e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon fontSize={"small"} />,
                    }}
                    sx={{
                      "& .MuiInputBase-root": {
                        height: 32,
                      },
                    }}
                  />
                </Box>
                <Box flex={1}>
                  <TextField
                    fullWidth
                    size={"small"}
                    placeholder={"Search current claims..."}
                    value={currentSearch}
                    onChange={(e) => setCurrentSearch(e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon fontSize={"small"} />,
                    }}
                    sx={{
                      "& .MuiInputBase-root": {
                        height: 32,
                      },
                    }}
                  />
                </Box>
              </Stack>

              <Stack direction={"row"} spacing={2}>
                <Box flex={1}>
                  <ClaimsTable
                    claims={priorClaims}
                    selectedIds={selectedPriorIds}
                    onToggleSelect={handleTogglePrior}
                    searchTerm={priorSearch}
                    title={`Prior Claims (${selectedPriorIds.length} selected)`}
                    linkedIds={linkedPriorIds}
                  />
                </Box>
                <Box flex={1}>
                  <ClaimsTable
                    claims={currentClaims}
                    selectedIds={selectedCurrentIds}
                    onToggleSelect={handleToggleCurrent}
                    searchTerm={currentSearch}
                    title={`Current Claims (${selectedCurrentIds.length} selected)`}
                    linkedIds={linkedCurrentIds}
                  />
                </Box>
              </Stack>

              <Paper variant={"outlined"} sx={{ padding: 1.5 }}>
                <Stack spacing={1.5}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    placeholder={"Add a comment about this match (optional)"}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    size={"small"}
                  />
                  <Button
                    variant={"contained"}
                    onClick={handleCreateMatch}
                    disabled={!canCreateMatch}
                    sx={{ alignSelf: "center" }}
                  >
                    {`Link ${selectedPriorIds.length} Prior with ${selectedCurrentIds.length} Current`}
                  </Button>
                </Stack>
              </Paper>
            </Stack>
          )}

          {currentTab === 1 && (
            <Box>
              {manualMatches.length === 0 ? (
                <Alert severity={"info"}>
                  {
                    "No manual links created yet. Switch to 'Create New Link' tab to start linking claims."
                  }
                </Alert>
              ) : (
                <Grid container spacing={2}>
                  {manualMatches.map((match) => (
                    <Grid item xs={4} key={match.match_id}>
                      <ManualMatchItem
                        match={match}
                        onDelete={() => handleDeleteMatch(match.match_id)}
                        priorClaims={priorClaims}
                        currentClaims={currentClaims}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleClose}
          variant={"contained"}
          color={"secondary"}
          disableElevation
          sx={{
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {"Cancel"}
        </Button>
        <Button
          variant={"contained"}
          color={"primary"}
          onClick={handleSave}
          disableElevation
          sx={{
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {`Apply ${manualMatches.length} manual match${
            manualMatches.length !== 1 ? "es" : ""
          }`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManualClaimsLinkerModal;
